import React, { useState } from 'react'
import { Tv, RefreshCw, Trash2, AlertTriangle } from 'lucide-react'
import { initializeChannelsCollection, clearAllChannels, resetChannelsCollection } from '@/utils/initializeChannels'
import { toast } from 'react-hot-toast'

const ChannelInitializer = () => {
  const [isInitializing, setIsInitializing] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const [isResetting, setIsResetting] = useState(false)

  const handleInitialize = async () => {
    setIsInitializing(true)
    try {
      await initializeChannelsCollection()
      toast.success('Channels collection initialized successfully!')
    } catch (error) {
      console.error('Failed to initialize channels:', error)
      toast.error('Failed to initialize channels collection')
    } finally {
      setIsInitializing(false)
    }
  }

  const handleClear = async () => {
    if (!window.confirm('Are you sure you want to delete ALL channels? This action cannot be undone.')) {
      return
    }
    
    setIsClearing(true)
    try {
      await clearAllChannels()
      toast.success('All channels cleared successfully!')
    } catch (error) {
      console.error('Failed to clear channels:', error)
      toast.error('Failed to clear channels')
    } finally {
      setIsClearing(false)
    }
  }

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset the channels collection? This will delete all existing channels and recreate sample ones.')) {
      return
    }
    
    setIsResetting(true)
    try {
      await resetChannelsCollection()
      toast.success('Channels collection reset successfully!')
    } catch (error) {
      console.error('Failed to reset channels:', error)
      toast.error('Failed to reset channels collection')
    } finally {
      setIsResetting(false)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center mb-4">
        <Tv className="w-5 h-5 text-blue-600 mr-2" />
        <h3 className="text-lg font-medium text-gray-900">Channels Collection Manager</h3>
      </div>
      
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <Tv className="w-5 h-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-blue-800">Initialize Collection</h4>
              <p className="text-sm text-blue-700 mt-1">
                Create sample channels if the collection is empty. This is safe to run multiple times.
              </p>
            </div>
          </div>
          <div className="mt-3">
            <button
              onClick={handleInitialize}
              disabled={isInitializing}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isInitializing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Initializing...
                </>
              ) : (
                <>
                  <Tv className="w-4 h-4 mr-2" />
                  Initialize Collection
                </>
              )}
            </button>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-yellow-800">Danger Zone</h4>
              <p className="text-sm text-yellow-700 mt-1">
                These actions will permanently delete data. Use with caution.
              </p>
            </div>
          </div>
          <div className="mt-3 space-x-3">
            <button
              onClick={handleClear}
              disabled={isClearing}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
            >
              {isClearing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Clearing...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All Channels
                </>
              )}
            </button>
            
            <button
              onClick={handleReset}
              disabled={isResetting}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
            >
              {isResetting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset Collection
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChannelInitializer 